import { useCallback, useEffect, useState } from 'react';
import FloatingInput from '../FloatingInput.jsx';
import toast from 'react-hot-toast';

export function BankDetailsForm({
    employeeData,
    setEmployeeData,
    onSaveSection,
    loading,
    feedback,
    darkMode,
    onCancel,
    onNext,
    isLastTab,
    readOnly = false,
    onSaveTrigger,
}) {
    const formData = employeeData;
    
    // State for IFSC validation errors
    const [ifscError, setIfscError] = useState('');
    
    // State for form validation errors
    const [formErrors, setFormErrors] = useState({});

    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;
        
        // Special validation for IFSC code
        if (name === 'ifscCode') {
            // Convert to uppercase and remove spaces
            const cleanValue = value.toUpperCase().replace(/\s/g, '');
            
            // Validate alphanumeric and length
            if (cleanValue && !/^[A-Z0-9]+$/.test(cleanValue)) {
                setIfscError('IFSC code should contain only letters and numbers');
            } else if (cleanValue && cleanValue.length !== 11) {
                setIfscError('IFSC code must be exactly 11 characters');
            } else {
                setIfscError('');
            }
            
            // Update with cleaned value
            setEmployeeData(prev => ({ ...prev, [name]: cleanValue }));
        } else {
            setEmployeeData(prev => ({ ...prev, [name]: value }));
        }
        
        // Real-time validation for all fields
        let error = null;
        if (name === 'bankName') {
            error = validateBankName(value);
        } else if (name === 'accountNumber') {
            error = validateAccountNumber(value);
        } else if (name === 'ifscCode') {
            error = validateIFSCCode(value);
        } else if (name === 'bankBranch') {
            error = validateBankBranch(value);
        }
        
        // Update form errors
        setFormErrors(prev => {
            if (error) {
                return { ...prev, [name]: error };
            } else {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            }
        });
    }, [setEmployeeData]);

    // Validation functions
    const validateBankName = (bankName) => {
        if (!bankName || bankName.trim() === '') {
            return 'Bank Name is required';
        }
        if (bankName.trim().length < 2) {
            return 'Bank Name must be at least 2 characters long';
        }
        if (!/^[A-Za-z\s]+$/.test(bankName.trim())) {
            return 'Bank Name can only contain letters and spaces';
        }
        return null;
    };

    const validateAccountNumber = (accountNumber) => {
        if (!accountNumber || accountNumber.trim() === '') {
            return 'Account Number is required';
        }
        if (accountNumber.trim().length < 5) {
            return 'Account Number must be at least 5 characters long';
        }
        if (accountNumber.trim().length > 20) {
            return 'Account Number cannot exceed 20 characters';
        }
        if (!/^[0-9]+$/.test(accountNumber.trim())) {
            return 'Account Number can only contain numbers';
        }
        return null;
    };

    const validateIFSCCode = (ifscCode) => {
        if (!ifscCode || ifscCode.trim() === '') {
            return 'IFSC Code is required';
        }
        if (ifscCode.trim().length !== 11) {
            return 'IFSC Code must be exactly 11 characters';
        }
        if (!/^[A-Za-z0-9]+$/.test(ifscCode.trim())) {
            return 'IFSC Code should contain only letters and numbers';
        }
        return null;
    };

    const validateBankBranch = (bankBranch) => {
        if (!bankBranch || bankBranch.trim() === '') {
            return 'Bank Branch is required';
        }
        if (bankBranch.trim().length < 2) {
            return 'Bank Branch must be at least 2 characters long';
        }
        if (!/^[A-Za-z\s]+$/.test(bankBranch.trim())) {
            return 'Bank Branch can only contain letters and spaces';
        }
        return null;
    };

    // Comprehensive form validation
    const validateForm = () => {
        const errors = {};
        
        errors.bankName = validateBankName(formData.bankName);
        errors.accountNumber = validateAccountNumber(formData.accountNumber);
        errors.ifscCode = validateIFSCCode(formData.ifscCode);
        errors.bankBranch = validateBankBranch(formData.bankBranch);
        
        // Remove null values (valid fields)
        Object.keys(errors).forEach(key => {
            if (errors[key] === null) {
                delete errors[key];
            }
        });
        
        setFormErrors(errors);
        console.log('Bank form validation result:', errors);
        return Object.keys(errors).length === 0;
    };

    const handleSave = async () => {
        console.log('Bank form save button clicked - starting validation...');
        
        // Validate the entire form before proceeding
        const isValid = validateForm();
        console.log('Bank form validation result:', isValid);
        
        if (!isValid) {
            console.log('Bank form validation failed:', formErrors);
            toast.error('Please fix the validation errors before saving.', { position: 'top-right' });
            return { saved: false }; // Don't save if there are validation errors
        }
        
        // Check if there are IFSC validation errors
        if (ifscError) {
            console.log('IFSC validation failed:', ifscError);
            toast.error('Please fix the IFSC code error before saving.', { position: 'top-right' });
            return { saved: false }; // Don't save if there are IFSC validation errors
        }
        
        console.log('Bank form validation passed - proceeding with save...');
        
        // Only send bank section fields - using API response field names
        const sectionData = {
            bankName: formData.bankName || '',
            accountNumber: formData.accountNumber || '',
            ifscCode: formData.ifscCode || '',
            bankBranch: formData.bankBranch || ''
        };
        
        try {
            await onSaveSection(sectionData);
            toast.success('Bank details saved successfully', { position: 'top-right' });
            // Only navigate to next tab if save is successful
            if (onNext && !isLastTab) {
                onNext();
            }
            return { saved: true };
        } catch (error) {
            console.error('Save failed:', error);
            const msg =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                'Failed to save. Please try again.';
            toast.error(msg, { position: 'top-right' });
            return { saved: false };
        }
    };

    useEffect(() => {
        if (typeof onSaveTrigger === 'function') {
            onSaveTrigger(handleSave);
        }
    }, [onSaveTrigger, handleSave]);

    return (
        <div className="space-y-4">
            <fieldset disabled={readOnly} className={readOnly ? 'opacity-75' : ''}>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-1">
                    <FloatingInput
                        id="bankName"
                        name="bankName"
                        label="Bank Name"
                        value={formData.bankName || ''}
                        onChange={handleInputChange}
                        placeholder="Enter bank name"
                        required
                    />
                    {formErrors.bankName && (
                        <div className="text-red-500 text-sm">{formErrors.bankName}</div>
                    )}
                </div>
                <div className="space-y-1">
                    <FloatingInput
                        id="accountNumber"
                        name="accountNumber"
                        label="Bank Account No"
                        value={formData.accountNumber || ''}
                        onChange={handleInputChange}
                        placeholder="Enter account number"
                        maxLength={20}
                        required
                    />
                    {formErrors.accountNumber && (
                        <div className="text-red-500 text-sm">{formErrors.accountNumber}</div>
                    )}
                </div>
                <div className="space-y-1">
                    <FloatingInput
                        id="ifscCode"
                        name="ifscCode"
                        label="IFSC Code"
                        value={formData.ifscCode || ''}
                        onChange={handleInputChange}
                        placeholder="e.g., SBIN0001234"
                        maxLength={11}
                        required
                    />
                    {(formErrors.ifscCode || ifscError) && (
                        <div className="text-red-500 text-sm">{formErrors.ifscCode || ifscError}</div>
                    )}
                </div>
                <div className="space-y-1">
                    <FloatingInput
                        id="bankBranch"
                        name="bankBranch"
                        label="Branch"
                        value={formData.bankBranch || ''}
                        onChange={handleInputChange}
                        placeholder="Enter branch name"
                        required
                    />
                    {formErrors.bankBranch && (
                        <div className="text-red-500 text-sm">{formErrors.bankBranch}</div>
                    )}
                </div>
            </div>
            {feedback && (
                <div className={`mt-2 ${feedback.type === 'success' ? 'text-emerald-600' : 'text-red-600'}`}>{feedback.message}</div>
            )}
            
            {/* Validation Summary - Show when form has validation errors */}
            {/* {Object.keys(formErrors).length > 0 && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
                    <h3 className="text-sm font-medium text-red-800 mb-2">Please fix the following errors before proceeding:</h3>
                    <ul className="text-sm text-red-700 space-y-1">
                        {Object.entries(formErrors).map(([field, error]) => (
                            <li key={field} className="flex items-start">
                                <span className="mr-2">•</span>
                                <span>{error}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )} */}
            </fieldset>
        </div>
    );
}